import React, { useEffect, useState } from "react";

import AOS from "aos";
import "aos/dist/aos.css";

import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaSave,
  FaEdit,
  FaTimes,
} from "react-icons/fa";

import "./Contact.css";
import { BASE_URL } from "../../Config/config";

export default function Contact() {
  const CONTACT_API = `${BASE_URL}/contact.php`;

  /* =====================================================
     CONTACT DATA
     ===================================================== */

  const [contactData, setContactData] = useState({
    phone: "",
    email: "",
    address: "",
    businessHours: "",
  });

  const [formData, setFormData] = useState({
    phone: "",
    email: "",
    address: "",
    businessHours: "",
  });

  /* =====================================================
     UI STATES
     ===================================================== */

  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* =====================================================
     FIXED SUPPORT MESSAGE
     ===================================================== */

  /* =====================================================
     FETCH CONTACT
     ===================================================== */

  const fetchContact = async () => {
    try {
      setLoading(true);

      const response = await fetch(CONTACT_API);

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Failed to fetch contact details");
      }

      const data = result.data;

      const formattedData = {
        phone: data.phone || "",
        email: data.email || "",
        address: data.address || "",
        businessHours: data.business_hours || "",
      };

      setContactData(formattedData);
      setFormData(formattedData);
    } catch (error) {
      console.error("FETCH CONTACT ERROR:", error);

      alert(error.message || "Unable to load contact details.");
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     INITIAL LOAD
     ===================================================== */

  useEffect(() => {
    fetchContact();
  }, []);
  useEffect(() => {
    AOS.init({
      duration: 700,
      once: true,
      easing: "ease-out-cubic",
      offset: 80,
    });
  }, []);
  /* =====================================================
     HANDLE INPUT
     ===================================================== */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =====================================================
     OPEN EDIT
     ===================================================== */

  const openEdit = () => {
    setFormData(contactData);
    setShowForm(true);
  };

  /* =====================================================
     CLOSE FORM
     ===================================================== */

  const closeForm = () => {
    if (saving) return;

    setFormData(contactData);
    setShowForm(false);
  };

  /* =====================================================
     SAVE CONTACT
     ===================================================== */

  const saveContact = async (e) => {
    e.preventDefault();

    if (saving) return;

    try {
      setSaving(true);

      const response = await fetch(CONTACT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          address: formData.address.trim(),
          businessHours: formData.businessHours.trim(),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Failed to save contact details");
      }

      setContactData(formData);

      setShowForm(false);

      alert(result.message || "Contact details saved successfully.");
    } catch (error) {
      console.error("SAVE CONTACT ERROR:", error);

      alert(error.message || "Unable to save contact details.");
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
     LOADING
     ===================================================== */

  if (loading) {
    return (
      <div className="admin-contact-page">
        <div className="admin-contact-loading">Loading contact details...</div>
      </div>
    );
  }

  return (
    <div className="admin-contact-page">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="admin-page-header">
        <div>
          <span className="admin-page-label">CONTACT MANAGEMENT</span>

          <h1>Contact Details</h1>

          <p>
            Manage the contact information displayed on the Star Computers
            website.
          </p>
        </div>

        <button type="button" className="admin-primary-btn" onClick={openEdit}>
          <FaEdit />
          Edit Contact
        </button>
      </div>

      {/* =====================================================
          CONTACT CARDS
      ===================================================== */}

      <div className="admin-contact-grid">
        {/* PHONE */}

        <div
          className="admin-contact-card"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <div className="admin-contact-icon">
            <FaPhoneAlt />
          </div>

          <div className="admin-contact-content">
            <span>PHONE NUMBER</span>

            <h3>{contactData.phone}</h3>

            <p>Primary business contact number</p>
          </div>
        </div>

        {/* EMAIL */}

        <div
          className="admin-contact-card"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <div className="admin-contact-icon">
            <FaEnvelope />
          </div>

          <div className="admin-contact-content">
            <span>EMAIL ADDRESS</span>

            <h3>{contactData.email}</h3>

            <p>Business enquiry email address</p>
          </div>
        </div>

        {/* ADDRESS */}

        <div
          className="admin-contact-card"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <div className="admin-contact-icon">
            <FaMapMarkerAlt />
          </div>

          <div className="admin-contact-content">
            <span>OFFICE ADDRESS</span>

            <h3>{contactData.address}</h3>

            <p>Business office location</p>
          </div>
        </div>

        {/* BUSINESS HOURS */}

        <div
          className="admin-contact-card"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <div className="admin-contact-icon">
            <FaClock />
          </div>

          <div className="admin-contact-content">
            <span>BUSINESS HOURS</span>

            <h3>{contactData.businessHours}</h3>

            <p>Working days and business timings</p>
          </div>
        </div>
      </div>

      {showForm && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            {/* MODAL HEADER */}

            <div className="admin-modal-header">
              <div>
                <span>CONTACT MANAGEMENT</span>

                <h2>Edit Contact Details</h2>
              </div>

              <button
                type="button"
                onClick={closeForm}
                className="admin-modal-close"
                disabled={saving}
              >
                <FaTimes />
              </button>
            </div>

            {/* FORM */}

            <form className="admin-contact-form" onSubmit={saveContact}>
              {/* PHONE */}

              <div className="admin-form-group">
                <label>
                  Phone Number
                  <span>*</span>
                </label>

                <div className="admin-input-wrapper">
                  <FaPhoneAlt />

                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 9824393921"
                    required
                  />
                </div>
              </div>

              {/* EMAIL */}

              <div className="admin-form-group">
                <label>
                  Email Address
                  <span>*</span>
                </label>

                <div className="admin-input-wrapper">
                  <FaEnvelope />

                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="info@example.com"
                    required
                  />
                </div>
              </div>

              {/* ADDRESS */}

              <div className="admin-form-group full-width">
                <label>
                  Office Address
                  <span>*</span>
                </label>

                <div className="admin-textarea-wrapper">
                  <FaMapMarkerAlt />

                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter complete office address"
                    rows="3"
                    required
                  />
                </div>
              </div>

              {/* BUSINESS HOURS */}

              <div className="admin-form-group full-width">
                <label>
                  Business Hours
                  <span>*</span>
                </label>

                <div className="admin-input-wrapper">
                  <FaClock />

                  <input
                    type="text"
                    name="businessHours"
                    value={formData.businessHours}
                    onChange={handleChange}
                    placeholder="Mon - Sat : 10:00 AM - 7:00 PM"
                    required
                  />
                </div>
              </div>

              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-cancel-btn"
                  onClick={closeForm}
                  disabled={saving}
                >
                  <FaTimes />
                  Cancel
                </button>

                <button
                  type="submit"
                  className="admin-save-btn"
                  disabled={saving}
                >
                  <FaSave />

                  {saving ? "Saving..." : "Save Contact Details"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
